import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { trpc } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router-dom";

const PAGE_SIZE = 50;

function ManageChats() {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");

  const {
    data: chatsData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
  } = trpc.chat.getPaginatedChats.useInfiniteQuery(
    projectId && user?.id
      ? {
          projectId,
          userId: user.id,
          limit: PAGE_SIZE,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
      staleTime: 0,
    },
  );

  const chats = React.useMemo(
    () => chatsData?.pages?.flatMap((page) => page.chats) ?? [],
    [chatsData],
  );

  const handleNewChat = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("chatId", "");
      return newParams;
    });
  };

  const handleChatClick = (chatId: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("chatId", chatId);
      return newParams;
    });
  };

  if (!projectId || !user?.id) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner variant="primary" size={24} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        Error Loading Chats
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner variant="primary" size={24} />
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        No Chats Found
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full p-3 gap-3">
      <h2 className="text-base font-semibold text-default">Your Chats</h2>

      <Button
        variant="secondary"
        size="base"
        layoutClassName="w-full"
        onClick={handleNewChat}
      >
        New Chat
      </Button>

      <div id="chats-scroll-container" className="flex-1 overflow-y-auto">
        <InfiniteScroll
          dataLength={chats.length}
          next={() => void fetchNextPage()}
          hasMore={hasNextPage ?? false}
          scrollableTarget="chats-scroll-container"
          loader={
            <div className="text-center p-4 flex justify-center items-center">
              <Spinner variant="primary" size={16} />
            </div>
          }
          style={{ overflow: "visible" }}
        >
          <div className="grid gap-1">
            {chats.map((chat) => (
              <div
                className={twMerge(
                  "p-2 text-default text-base cursor-pointer hover:bg-sheet transition-colors rounded-md",
                  chatId === chat.id && "bg-sheet border-border font-medium",
                )}
                key={chat.id}
                onClick={() => handleChatClick(chat.id)}
              >
                {chat.title}
              </div>
            ))}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
}

export default ManageChats;
